import Config from 'config';
import { BTC_NETWORK_MAIN } from './BitcoinConstants';

// Import only types to avoid bundling of lazy-loaded libs.
import type { ElectrumClient, ElectrumClientOptions } from '@nimiq/electrum-client';
import type { Transaction as BitcoinJsTransaction } from 'bitcoinjs-lib';

let electrumClientPromise: Promise<ElectrumClient> | null = null;

/**
 * Get a singleton Electrum client. This singleton should be used throughout the whole app, such that consensus has
 * only to be established once. The Electrum client library is lazy-loaded on demand. Optionally wait for consensus.
 */
export async function getElectrumClient(waitForConsensus: boolean = true) {
    electrumClientPromise = electrumClientPromise || (async () => {
        const { GenesisConfig, Network, ElectrumClient: Client } = await import(
            /*webpackChunkName: "electrum-client"*/ '@nimiq/electrum-client');

        try {
            GenesisConfig[Config.bitcoinNetwork === BTC_NETWORK_MAIN ? 'main' : 'test']();
        } catch (e) {
            // GenesisConfig already initialized. Check whether it's for the correct network.
            if ((Config.bitcoinNetwork === BTC_NETWORK_MAIN) !== (GenesisConfig.NETWORK_NAME === Network.MAIN)) {
                throw new Error('Wrong Electrum client GenesisConfig initialized.');
            }
        }

        const options: Partial<ElectrumClientOptions> = Config.bitcoinNetwork === BTC_NETWORK_MAIN ? {
            extraSeedPeers: [{
                host: 'electrumx.nimiq.com',
                wssPath: 'electrumx',
                ports: { wss: 443, ssl: 50002, tcp: 50001 },
                ip: '',
                version: '',
                highPriority: true,
            }, {
                host: 'btccore-main.bdnodes.net',
                ports: { wss: null, ssl: 50002, tcp: null },
                ip: '',
                version: '',
            }],
            websocketProxy: {
                tcp: 'wss://electrum.nimiq.com:50001',
                ssl: 'wss://electrum.nimiq.com:50002',
            },
        } : {};

        return new Client(options);
    })();

    let client: ElectrumClient;
    try {
        client = await electrumClientPromise;
    } catch (e) {
        electrumClientPromise = null;
        throw e;
    }

    if (waitForConsensus) {
        await client.waitForConsensusEstablished();
        console.log('BTC Consensus established');
    }

    return client;
}

export async function fetchTransaction(transactionHash: string): Promise<BitcoinJsTransaction> {
    const [electrum, transactionFromPlain] = await Promise.all([
        getElectrumClient(),
        import(/*webpackChunkName: "electrum-client"*/ '@nimiq/electrum-client')
            .then((module) => module.transactionFromPlain),
    ]);

    const fetchedTransaction = await electrum.getTransaction(transactionHash);
    return transactionFromPlain(fetchedTransaction);
}
